/**
 * Step 5 (Assign) of the Build Itinerary wizard.
 *
 * The assignment grid: the tournament's games grouped by day, each with a coach
 * picker. It opens on the games involving a team or player the coach follows, with
 * an "All games" tab for the rest. "✨ Auto Assign" asks the server for a balanced,
 * conflict-aware proposal (biased to cover the most-starred games first) and drops
 * it into the grid for tweaking. Like "Assign ... to me", it plans only the games on
 * screen, and games off screen keep whatever they had. A workload rail shows games-per-coach and doubles
 * as the pool selector for Auto-Assign. "Save & Finish" persists every changed game
 * via the per-game assignee PUT, then leaves the wizard.
 *
 * "Assign ... to me" covers only the games on screen. It used to add the coach as an
 * event-level assignee, which covers every game in the tournament, so a coach who
 * followed a few teams ended up on every game. It skips any game that would overlap one
 * the coach already holds.
 *
 * Off-screen games still count: Auto Assign sends their current coaches as draftAssignees,
 * and the double-booking notes check every game in the tournament. Whole-tournament
 * coaches show on every row as locked chips, matching what the saved itinerary shows.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../../../hooks/useAuth';
import {
  formatEventClockTime,
  timeZoneAbbrev,
} from '../../../../../utils/calendarTime';
import { getOrganizationMembers } from '../../../../../services/calendar';
import {
  autoAssignTournament,
  setGamesAssignees,
} from '../../../../../services/tournament-games';
import { getSafeErrorMessage } from '../../../../../utils/errorHandler';
import type { OrganizationMember } from '../../../../../types/api';
import type { TournamentGameDto } from '../../../../../types/tournament-games';
import { CoachSelectField } from '../ScheduleFields';
import { useDismiss } from '../useDismiss';
import { Avatar, PlusIcon } from '../CoachAvatar';
import { useTournamentGames } from './useTournamentGames';
import { WizardFooter } from './WizardFooter';
import { groupGamesByDay, formatDayHeading, isFollowRelevantGame, normalizeTeamName } from './itinerary';
import {
  seedDraft,
  applyProposal,
  setGameCoaches,
  computeWorkload,
  changedGameIds,
  assignmentsToCsv,
  offScopeDraft,
  tournamentCoaches,
  findCoachConflicts,
  assignGamesToCoach,
  type AssignmentDraft,
  type CoachConflict,
  type TournamentCoach,
} from './autoAssign';
import { tourAnchor } from '../../../../tours/tourAnchors';
import { useTour } from '../../../../tours/useTour';
import { BUILD_ITINERARY_FEATURE, BUILD_ITINERARY_TOURS } from './buildItineraryTours';

interface AssignStepProps {
  calendarEventId: string | undefined;
  /** Called to finish; the (possibly renamed) final tournament title is passed up to publish. */
  /**
   * Finalize the wizard (publish the draft, or complete-setup on a distributed
   * copy). May be async; rejects if the finalize call fails so this step can keep
   * the user here with the error shown instead of navigating away silently.
   */
  onFinish: (title?: string) => void | Promise<void>;
  onBack?: () => void;
  canGoBack?: boolean;
}

const EMPTY_GAMES: TournamentGameDto[] = [];

export function AssignStep({ calendarEventId, onFinish, onBack, canGoBack }: AssignStepProps) {
  const { user } = useAuth();
  const currentUserId = user?.profile?.id;

  const gamesState = useTournamentGames(calendarEventId);
  const games = gamesState.status === 'ready' ? gamesState.games : EMPTY_GAMES;

  // The toolbar and Save & Finish render only once the games have loaded.
  useTour(BUILD_ITINERARY_FEATURE, {
    ready: Boolean(calendarEventId) && gamesState.status === 'ready',
    hasData: false,
    pageTourKey: BUILD_ITINERARY_TOURS.assign,
    dataTourKey: null,
  });

  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [pool, setPool] = useState<Set<string>>(new Set());

  // Grid scope: default to the games involving a team or player the coach follows, with an "All games"
  // tab to reveal the full tournament so any game can still be assigned.
  const [followedOnly, setFollowedOnly] = useState(true);

  // gameId -> draft coach ids (client-side until saved); `saved` is the baseline
  // we diff against so Save only PUTs games the coach actually changed.
  const [draft, setDraft] = useState<AssignmentDraft>({});
  const [saved, setSaved] = useState<AssignmentDraft>({});
  const seededForRef = useRef<string | null>(null);

  const [autoAssigning, setAutoAssigning] = useState(false);
  const [autoAssignError, setAutoAssignError] = useState('');
  const [unassignableIds, setUnassignableIds] = useState<string[]>([]);
  // Whole-tournament coaches reported by the last auto-assign (an older API omits them).
  const [serverTournamentCoachIds, setServerTournamentCoachIds] = useState<string[]>([]);
  // How many games "Assign ... to me" left alone because they overlap one the coach holds.
  const [assignMeSkipped, setAssignMeSkipped] = useState<number | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // The final tournament name, editable here (the finalize step). Seeded from the current title.
  const [name, setName] = useState('');

  // The coaching-workload / Auto-Assign pool now lives in a toolbar dropdown so the grid is full-width.
  const [poolOpen, setPoolOpen] = useState(false);
  const poolRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!poolOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (poolRef.current && !poolRef.current.contains(e.target as Node)) setPoolOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [poolOpen]);
  const poolCount = useMemo(
    () => members.filter((m) => pool.has(m.id)).length,
    [members, pool],
  );

  // Load the org's members once for the pickers + workload; seed the auto-assign
  // pool to everyone. Only needed once there's a tournament to assign against.
  useEffect(() => {
    if (!calendarEventId) return;
    let active = true;
    getOrganizationMembers()
      .then((m) => {
        if (!active) return;
        setMembers(m);
        setPool(new Set(m.map((x) => x.id)));
      })
      .catch(() => {
        /* members are optional polish; the grid still renders without them */
      });
    return () => {
      active = false;
    };
  }, [calendarEventId]);

  // Seed the draft + baseline from each game's current coaches, once per loaded
  // tournament (re-seeds if the tournament id changes).
  useEffect(() => {
    if (gamesState.status !== 'ready') return;
    if (seededForRef.current === calendarEventId) return;
    const initial = seedDraft(gamesState.games);
    setDraft(initial);
    setSaved(initial);
    setName(gamesState.title);
    setUnassignableIds([]);
    setServerTournamentCoachIds([]);
    setAssignMeSkipped(null);
    seededForRef.current = calendarEventId ?? null;
  }, [gamesState, calendarEventId]);

  // Games involving a team the coach follows or a player they follow (a game's matchedPlayers ARE the
  // followed prospects on its teams). Backs the "Followed" tab, which stays the default even when it is
  // empty: silently showing every game there is how "assign to me" ended up covering games the coach
  // never followed. The empty state points them at "All games" instead.
  const followedTeamIds = useMemo(
    () =>
      new Set(
        gamesState.status === 'ready' ? gamesState.followedTeamIds : [],
      ),
    [gamesState],
  );
  const followedTeamNames = useMemo(
    () =>
      new Set(
        (gamesState.status === 'ready' ? gamesState.followedTeamNames : []).map(
          normalizeTeamName,
        ),
      ),
    [gamesState],
  );
  const followedGames = useMemo(
    () =>
      games.filter((game) =>
        isFollowRelevantGame(game, followedTeamIds, followedTeamNames),
      ),
    [games, followedTeamIds, followedTeamNames],
  );
  const showFollowedOnly = followedOnly;
  const visibleGames = showFollowedOnly ? followedGames : games;

  // Coaches on the whole tournament: shown on every row as locked chips so the grid matches the
  // saved itinerary, and left out of the conflict check (they are on every game by design).
  const wholeTournamentCoaches = useMemo(
    () => tournamentCoaches(games, serverTournamentCoachIds, members),
    [games, serverTournamentCoachIds, members],
  );
  const wholeTournamentIds = useMemo(
    () => new Set(wholeTournamentCoaches.map((c) => c.userId)),
    [wholeTournamentCoaches],
  );

  // Double bookings across every game in the tournament, not only the ones on screen.
  const conflicts = useMemo(
    () => findCoachConflicts(games, draft, wholeTournamentIds),
    [games, draft, wholeTournamentIds],
  );
  const hiddenConflictCount = useMemo(() => {
    const shown = new Set(visibleGames.map((g) => g.id));
    return [...conflicts.keys()].filter((id) => !shown.has(id)).length;
  }, [conflicts, visibleGames]);

  // True when the current coach is already on every game on screen, so the button reads as done.
  const allShownAreMine =
    currentUserId != null &&
    visibleGames.length > 0 &&
    (wholeTournamentIds.has(currentUserId) ||
      visibleGames.every((g) => (draft[g.id] ?? []).includes(currentUserId)));

  const dayGroups = useMemo(
    () =>
      groupGamesByDay(
        visibleGames.map((game) => ({ game, matchedSelectedPlayers: [] })),
      ),
    [visibleGames],
  );

  const workload = useMemo(
    () => computeWorkload(draft, members),
    [draft, members],
  );

  const gamesById = useMemo(() => new Map(games.map((g) => [g.id, g])), [games]);

  const warnings = useMemo(() => {
    if (unassignableIds.length === 0) return [];
    return unassignableIds
      .map((id) => gamesById.get(id))
      .filter((g): g is TournamentGameDto => Boolean(g));
  }, [unassignableIds, gamesById]);

  const pendingChanges = useMemo(
    () => changedGameIds(saved, draft).length,
    [saved, draft],
  );
  // Coverage reflects what's on screen, so the legend stays coherent with the active tab.
  const covered = useMemo(
    () => visibleGames.filter((g) => (draft[g.id] ?? []).length > 0).length,
    [visibleGames, draft],
  );

  const handleAutoAssign = async () => {
    if (!calendarEventId) return;
    const poolIds = members.filter((m) => pool.has(m.id)).map((m) => m.id);
    if (poolIds.length === 0) {
      setAutoAssignError('Pick at least one coach to include in Auto-Assign.');
      return;
    }
    // Only the games on screen. An older API ignores gameIds and plans every game, so the
    // proposal is scoped here as well.
    const scopeGameIds = visibleGames.map((g) => g.id);
    if (scopeGameIds.length === 0) return;
    const scope = new Set(scopeGameIds);
    setAutoAssigning(true);
    setAutoAssignError('');
    setAssignMeSkipped(null);
    try {
      // Off-screen games go along with their current (maybe unsaved) coaches so the planner
      // never puts a coach on a game that clashes with one they hold on another tab.
      const res = await autoAssignTournament(
        calendarEventId,
        poolIds,
        scopeGameIds,
        offScopeDraft(games, draft, scopeGameIds, saved),
      );
      const unassignable = res.unassignableGameIds ?? [];
      setDraft((prev) =>
        applyProposal(prev, res.assignments ?? [], unassignable, scopeGameIds),
      );
      setUnassignableIds(unassignable.filter((id) => scope.has(id)));
      setServerTournamentCoachIds(res.tournamentCoachUserIds ?? []);
    } catch (err) {
      setAutoAssignError(
        getSafeErrorMessage(
          err,
          "Auto-assign couldn't build a proposal. Please try again.",
        ),
      );
    } finally {
      setAutoAssigning(false);
    }
  };

  // Put the current coach on each game on screen, keeping anyone already assigned. Draft only, like
  // every other grid change, so it shows up in the rows and saves with "Save & Finish".
  // Games that would overlap one the coach already holds (anywhere in the tournament) are skipped,
  // and the coach is told how many.
  const handleAssignMe = () => {
    if (!currentUserId) return;
    const result = assignGamesToCoach(draft, games, visibleGames, currentUserId);
    setDraft(result.draft);
    setAssignMeSkipped(result.skipped);
  };

  const handleSaveAndFinish = async () => {
    if (!calendarEventId) return;
    const ids = changedGameIds(saved, draft);
    setSaving(true);
    setSaveError('');
    try {
      // One request for the whole grid, not one per game. Besides the round trips,
      // the server groups its "assigned to you" push per coach per request, so a
      // loop would alert a coach once for every game they picked up.
      if (ids.length > 0) {
        await setGamesAssignees(
          calendarEventId,
          ids.map((gameId) => ({ gameId, assigneeUserIds: draft[gameId] ?? [] })),
        );
      }
      setSaved(draft);
      // Finalize last so a failed publish/complete-setup surfaces here (below)
      // and keeps the user on this step to retry, rather than navigating away.
      await onFinish(name.trim() || undefined);
    } catch (err) {
      setSaveError(
        getSafeErrorMessage(
          err,
          "We couldn't finish building your itinerary. Please try again.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    const csv = assignmentsToCsv(games, draft, members);
    const url = URL.createObjectURL(
      new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = 'assignments.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Switching tabs changes which games "Assign me" covered, so its skipped note no longer applies.
  const showFollowed = (on: boolean) => {
    setFollowedOnly(on);
    setAssignMeSkipped(null);
  };

  const handleRowChange = (gameId: string, next: string[]) => {
    setDraft((prev) => setGameCoaches(prev, gameId, next));
    setAssignMeSkipped(null);
  };

  const togglePool = (id: string) =>
    setPool((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (!calendarEventId) {
    return (
      <div className="wiz-error-box" data-testid="assign-no-tournament">
        <p>
          The imported schedule has not been confirmed into a tournament yet.
          Finish importing on the Event step first.
        </p>
      </div>
    );
  }

  if (gamesState.status === 'loading') {
    return (
      <div className="wiz-players-state" data-testid="assign-loading">
        <span className="wiz-spinner" aria-hidden="true" />
        <span>Loading the games to assign...</span>
      </div>
    );
  }

  if (gamesState.status === 'error') {
    return (
      <div className="wiz-error-box" data-testid="assign-error">
        <p role="alert">{gamesState.message}</p>
      </div>
    );
  }

  const hasGames = dayGroups.length > 0;

  return (
    <div data-testid="assign-step">
      <WizardFooter
        onBack={onBack}
        canGoBack={canGoBack}
        legend={
          <>
            {covered} of {visibleGames.length} games covered
            {pendingChanges > 0
              ? ` · ${pendingChanges} unsaved ${
                  pendingChanges === 1 ? 'change' : 'changes'
                }`
              : ' · all changes saved'}
          </>
        }
      >
        <button
          type="button"
          className="wiz-btn wiz-btn--primary"
          onClick={handleSaveAndFinish}
          disabled={saving || autoAssigning}
          data-testid="assign-finish"
          {...tourAnchor('build-itinerary.assign.finish')}
        >
          {saving ? 'Saving…' : 'Save & Finish'}
        </button>
      </WizardFooter>

      <div className="wiz-field">
        <label htmlFor="assign-name">Event name</label>
        <input
          id="assign-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name this event"
          data-testid="assign-name"
        />
      </div>

      <div className="wiz-toolbar">
        <button
          type="button"
          className="wiz-btn wiz-btn--primary"
          onClick={handleAutoAssign}
          disabled={autoAssigning || saving || visibleGames.length === 0}
          data-testid="assign-auto"
          {...tourAnchor('build-itinerary.assign.auto')}
        >
          {autoAssigning ? 'Assigning…' : '✨ Auto Assign'}
        </button>

        <button
          type="button"
          className="wiz-btn"
          onClick={handleAssignMe}
          disabled={
            autoAssigning ||
            saving ||
            !currentUserId ||
            visibleGames.length === 0 ||
            allShownAreMine
          }
          data-testid="assign-all-to-me"
        >
          {allShownAreMine
            ? '✓ Assigned to you'
            : visibleGames.length === 1
              ? 'Assign this game to me'
              : visibleGames.length > 1
                ? `Assign these ${visibleGames.length} games to me`
                : 'Assign games to me'}
        </button>

        <div className="wiz-pool" ref={poolRef}>
          <button
            type="button"
            className="wiz-btn"
            aria-expanded={poolOpen}
            onClick={() => setPoolOpen((v) => !v)}
            data-testid="assign-pool-toggle"
            {...tourAnchor('build-itinerary.assign.pool')}
          >
            Coaches ({poolCount}) ▾
          </button>
          {poolOpen && (
            <div className="wiz-pool__menu">
              <p className="wiz-sub wiz-sub--sm">
                Toggle who Auto-Assign can use. Counts reflect the current plan.
              </p>
              {workload.length === 0 ? (
                <p className="wiz-sub">No coaches found in your organization.</p>
              ) : (
                <ul className="wiz-workload" data-testid="assign-workload">
                  {workload.map(({ member, gameCount }) => (
                    <li key={member.id} className="wiz-workload__row">
                      <label className="wiz-workload__pick">
                        <input
                          type="checkbox"
                          checked={pool.has(member.id)}
                          onChange={() => togglePool(member.id)}
                        />
                        <span className="wiz-workload__name">{member.displayName}</span>
                      </label>
                      <span
                        className="wiz-workload__count"
                        data-testid={`assign-workload-${member.id}`}
                      >
                        {gameCount} {gameCount === 1 ? 'game' : 'games'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div
          className="wiz-seg"
          role="group"
          aria-label="Show followed games or all games"
        >
          <button
            type="button"
            className={showFollowedOnly ? 'wiz-seg__on' : ''}
            aria-pressed={showFollowedOnly}
            onClick={() => showFollowed(true)}
            disabled={autoAssigning || saving}
            data-testid="assign-followed"
          >
            Followed ({followedGames.length})
          </button>
          <button
            type="button"
            className={!showFollowedOnly ? 'wiz-seg__on' : ''}
            aria-pressed={!showFollowedOnly}
            onClick={() => showFollowed(false)}
            disabled={autoAssigning || saving}
            data-testid="assign-all-games"
          >
            All games
          </button>
        </div>

        <span className="wiz-toolbar__spacer" />
        <button
          type="button"
          className="wiz-btn"
          onClick={handleExport}
          disabled={games.length === 0}
          data-testid="assign-export"
        >
          Export CSV
        </button>
      </div>

      <div role="status" aria-live="polite">
        {assignMeSkipped != null && assignMeSkipped > 0 && (
          <div className="wiz-note wiz-note--review wiz-note--warn" data-testid="assign-me-skipped">
            <span className="wiz-note__icon" aria-hidden="true">!</span>
            <p className="wiz-note__text">
              Skipped {assignMeSkipped}{' '}
              {assignMeSkipped === 1 ? 'game that overlaps' : 'games that overlap'} a game you
              already have.
            </p>
          </div>
        )}
      </div>

      {hiddenConflictCount > 0 && (
        <div className="wiz-note wiz-note--review wiz-note--warn" data-testid="assign-hidden-conflicts">
          <span className="wiz-note__icon" aria-hidden="true">!</span>
          <p className="wiz-note__text">
            {hiddenConflictCount} {hiddenConflictCount === 1 ? 'game' : 'games'} not shown here{' '}
            {hiddenConflictCount === 1 ? 'has' : 'have'} a coach on two games at once. Switch to
            All games to fix {hiddenConflictCount === 1 ? 'it' : 'them'}.
          </p>
        </div>
      )}

      {autoAssignError && (
        <div className="wiz-error-box" data-testid="assign-auto-error">
          <p role="alert">{autoAssignError}</p>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="wiz-note wiz-note--review" data-testid="assign-warnings">
          <div>
            <b>
              {warnings.length}{' '}
              {warnings.length === 1 ? 'game' : 'games'} couldn't be auto-assigned
              without double-booking a coach.
            </b>{' '}
            Add more coaches to the pool or assign these manually:
            <ul className="wiz-warn-list">
              {warnings.map((g) => (
                <li key={g.id}>
                  {formatEventClockTime(g)} · {g.homeTeamNameRaw} vs{' '}
                  {g.awayTeamNameRaw}
                  {g.fieldName ? ` · ${g.fieldName}` : ''}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {hasGames ? (
        <div className="wiz-card">
          {dayGroups.map((group) => (
            <div key={group.dayKey}>
              <div className="wiz-daygroup">
                {formatDayHeading(group.dayKey)} · {group.games.length}{' '}
                {group.games.length === 1 ? 'game' : 'games'}
              </div>
              <table className="wiz-table wiz-table--assign">
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Matchup</th>
                    <th scope="col">Field</th>
                    <th scope="col">Assign coaches</th>
                  </tr>
                </thead>
                <tbody>
                  {group.games.map(({ game }) => (
                    <AssignGameRow
                      key={game.id}
                      game={game}
                      members={members}
                      currentUserId={currentUserId}
                      wholeTournamentCoaches={wholeTournamentCoaches}
                      conflicts={conflicts.get(game.id) ?? []}
                      gamesById={gamesById}
                      value={draft[game.id] ?? []}
                      disabled={autoAssigning || saving}
                      onChange={(next) => handleRowChange(game.id, next)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      ) : showFollowedOnly && games.length > 0 ? (
        <div className="wiz-error-box" data-testid="assign-followed-empty">
          <p>
            None of these games involve a team or player you follow. Follow them on the
            earlier steps, or switch to All games to assign the full schedule.
          </p>
        </div>
      ) : (
        <div className="wiz-error-box" data-testid="assign-empty">
          <p>
            This tournament has no games to assign yet. Go back to the Event step
            and import a schedule.
          </p>
        </div>
      )}

      {saveError && (
        <div className="wiz-error-box" data-testid="assign-save-error">
          <p role="alert">{saveError}</p>
        </div>
      )}
    </div>
  );
}

interface AssignGameRowProps {
  game: TournamentGameDto;
  members: OrganizationMember[];
  currentUserId?: string;
  /** Coaches on the whole tournament: locked chips, not removable per game. */
  wholeTournamentCoaches: TournamentCoach[];
  /** Game-level coaches on this game who also hold an overlapping game. */
  conflicts: CoachConflict[];
  gamesById: Map<string, TournamentGameDto>;
  value: string[];
  /** True while Auto Assign is running, so a manual edit cannot race the proposal. */
  disabled?: boolean;
  onChange: (next: string[]) => void;
}

/**
 * "Jordan has another game at 11:00 AM, Hawks vs Bears", with the other game's zone when it
 * differs.
 */
function conflictText(
  conflict: CoachConflict,
  game: TournamentGameDto,
  gamesById: Map<string, TournamentGameDto>,
  byId: Map<string, OrganizationMember>,
): string {
  const name = byId.get(conflict.coachUserId)?.displayName ?? 'A coach';
  const other = gamesById.get(conflict.otherGameId);
  if (!other) return `${name} has another game at the same time`;
  const time = formatEventClockTime(other);
  const tz =
    other.timeZone !== game.timeZone
      ? timeZoneAbbrev(other.timeZone, new Date(other.startsAtUtc))
      : '';
  const matchup = `${other.homeTeamNameRaw} vs ${other.awayTeamNameRaw}`;
  return `${name} has another game at ${time}${tz ? ` ${tz}` : ''}, ${matchup}`;
}

/** One game row: tz-aware time, matchup, field, and a coach picker bound to the draft. */
function AssignGameRow({
  game,
  members,
  currentUserId,
  wholeTournamentCoaches,
  conflicts,
  gamesById,
  value,
  disabled = false,
  onChange,
}: AssignGameRowProps) {
  const time = formatEventClockTime(game);
  const tz = timeZoneAbbrev(game.timeZone, new Date(game.startsAtUtc));
  const [pickerOpen, setPickerOpen] = useState(false);
  const assignRef = useDismiss<HTMLDivElement>(pickerOpen, () => setPickerOpen(false));
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const lockedIds = useMemo(
    () => new Set(wholeTournamentCoaches.map((c) => c.userId)),
    [wholeTournamentCoaches],
  );
  // Whole-tournament coaches already cover this game, so the picker does not offer them again
  // (unless the game also lists them itself, so they can still be unchecked).
  const pickerMembers = useMemo(
    () => members.filter((m) => !lockedIds.has(m.id) || value.includes(m.id)),
    [members, lockedIds, value],
  );
  return (
    <tr data-testid={`assign-game-${game.id}`}>
      <td>
        {time}
        {tz && <div className="wiz-tzhint">{tz}</div>}
      </td>
      <td>
        {game.homeTeamNameRaw} vs {game.awayTeamNameRaw}
      </td>
      <td>{game.fieldName || '—'}</td>
      <td>
        {/* Jira-style: assigned coaches as avatars + a "+" circle that opens the
            coach-name list (removal is done by unchecking there). */}
        <div className="sf-assign" ref={assignRef}>
          {wholeTournamentCoaches.map((c) => (
            <span
              key={`t-${c.userId}`}
              className="wiz-tchip"
              role="img"
              aria-label={`${c.displayName}, whole tournament`}
              title={`${c.displayName}, whole tournament`}
              data-testid={`assign-tournament-coach-${game.id}-${c.userId}`}
            >
              <Avatar id={c.userId} initials={c.initials} ring={c.userId === currentUserId} />
              <span className="wiz-tchip__label">Whole tournament</span>
            </span>
          ))}
          {value.filter((id) => !lockedIds.has(id)).map((id) => {
            const m = byId.get(id);
            return (
              <Avatar
                key={id}
                id={id}
                initials={m?.initials ?? '?'}
                ring={id === currentUserId}
                title={m?.displayName}
              />
            );
          })}
          <button
            type="button"
            className={`sf-avatar sf-assign__add${pickerOpen ? ' sf-assign__add--on' : ''}`}
            onClick={() => setPickerOpen((v) => !v)}
            disabled={disabled}
            aria-expanded={pickerOpen && !disabled}
            aria-label="Add a coach"
            title="Add a coach"
            data-testid={`assign-add-coach-${game.id}`}
          >
            <PlusIcon />
          </button>
          {pickerOpen && !disabled && (
            <CoachSelectField
              members={pickerMembers}
              value={value}
              onChange={onChange}
              currentUserId={currentUserId}
              menuOnly
            />
          )}
        </div>
        {conflicts.length > 0 && (
          <ul className="wiz-conflicts" data-testid={`assign-conflict-${game.id}`}>
            {conflicts.map((c) => (
              <li key={c.coachUserId} className="wiz-conflict">
                <span className="wiz-conflict__icon" aria-hidden="true">!</span>
                <span>{conflictText(c, game, gamesById, byId)}</span>
              </li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}
